import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { money } from '../lib/util';
import type { MenuItem } from '../data/menu';

export interface SelectedModifier {
  modifierSetId: string;
  optionId: string;
  name: string;
  price: number;
}

interface ModifierOption {
  _id: string;
  name: string;
  price: number;
}

interface ModifierSetShape {
  _id: string;
  name: string;
  required?: boolean;
  minSelections?: number;
  maxSelections?: number;
  options: ModifierOption[];
}

/** Modal for choosing an item's modifier sets (size, spice level, add-ons, etc.)
    before it goes into the cart. Reads `item.raw.modifierSets` from the SDK. */
export function ModifierModal({
  item,
  onClose,
  onConfirm,
}: {
  item: MenuItem;
  onClose: () => void;
  onConfirm: (selected: SelectedModifier[], qty: number) => void;
}) {
  const modifierSets: ModifierSetShape[] = Array.isArray(item.raw?.modifierSets) ? item.raw.modifierSets : [];
  const [selections, setSelections] = useState<Record<string, SelectedModifier[]>>({});
  const [qty, setQty] = useState(1);

  function toggleOption(set: ModifierSetShape, opt: ModifierOption) {
    setSelections((prev) => {
      const current = prev[set._id] ?? [];
      const isSelected = current.some((s) => s.optionId === opt._id);
      const max = set.maxSelections ?? 1;
      let next: SelectedModifier[];
      if (max === 1) {
        next = isSelected ? [] : [{ modifierSetId: set._id, optionId: opt._id, name: opt.name, price: opt.price }];
      } else if (isSelected) {
        next = current.filter((s) => s.optionId !== opt._id);
      } else if (current.length >= max) {
        return prev;
      } else {
        next = [...current, { modifierSetId: set._id, optionId: opt._id, name: opt.name, price: opt.price }];
      }
      return { ...prev, [set._id]: next };
    });
  }

  const allSelected = Object.values(selections).flat();
  const modifierTotal = allSelected.reduce((sum, s) => sum + s.price, 0);
  const unitPrice = item.price + modifierTotal;

  const missingRequired = modifierSets.filter((set) => {
    if (!set.required) return false;
    const count = (selections[set._id] ?? []).length;
    const min = set.minSelections ?? 1;
    return count < min;
  });

  function handleConfirm() {
    if (missingRequired.length > 0) return;
    onConfirm(allSelected, qty);
  }

  return (
    <AnimatePresence>
      <motion.div
        onClick={onClose}
        className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center"
        style={{ background: 'var(--pe-scrim)', backdropFilter: 'blur(4px)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label={`Customize ${item.name}`}
          className="w-full sm:max-w-md sm:mx-4 max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl"
          style={{ background: 'var(--color-surface)', boxShadow: 'var(--pe-shadow-lg)' }}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div
            className="flex items-start justify-between gap-3 px-6 pt-6 pb-4"
            style={{ borderBottom: '1px solid var(--color-line)' }}
          >
            <div>
              <h3 className="font-display text-ink" style={{ fontSize: '1.3rem' }}>{item.name}</h3>
              <p className="tnum text-sm mt-0.5" style={{ color: 'var(--color-accent-deep)' }}>{money(item.price)}</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="grid place-items-center w-9 h-9 rounded-full shrink-0"
              style={{ background: 'var(--color-card-2)' }}
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-6">
            {modifierSets.map((set) => {
              const current = selections[set._id] ?? [];
              const isSingle = (set.maxSelections ?? 1) === 1;
              return (
                <div key={set._id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="font-semibold text-ink" style={{ fontSize: '0.98rem' }}>{set.name}</h4>
                    <span
                      className="text-xs shrink-0"
                      style={{ color: set.required ? 'var(--color-spice)' : 'var(--color-faint)' }}
                    >
                      {set.required
                        ? `Required${(set.minSelections ?? 1) > 1 ? ` · pick ${set.minSelections}` : ''}`
                        : 'Optional'}
                      {!isSingle && set.maxSelections ? ` · up to ${set.maxSelections}` : ''}
                    </span>
                  </div>
                  <div className="mt-2.5 space-y-2">
                    {set.options.map((opt) => {
                      const checked = current.some((s) => s.optionId === opt._id);
                      return (
                        <button
                          key={opt._id}
                          type="button"
                          onClick={() => toggleOption(set, opt)}
                          className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl transition-colors"
                          style={
                            checked
                              ? { background: 'var(--color-card-2)', border: '1.5px solid var(--color-accent)' }
                              : { border: '1.5px solid var(--color-line-2)' }
                          }
                        >
                          <span className="flex items-center gap-2.5 text-sm text-ink text-left">
                            <span
                              className="grid place-items-center shrink-0"
                              style={{
                                width: '1.1rem',
                                height: '1.1rem',
                                borderRadius: isSingle ? '999px' : '5px',
                                border: `1.5px solid ${checked ? 'var(--color-accent)' : 'var(--color-line-2)'}`,
                                background: checked ? 'var(--color-accent)' : 'transparent',
                              }}
                            >
                              {checked && <Check size={11} strokeWidth={3} style={{ color: 'var(--color-on-accent)' }} />}
                            </span>
                            {opt.name}
                          </span>
                          {opt.price > 0 && (
                            <span className="tnum text-sm shrink-0" style={{ color: 'var(--color-ink-2)' }}>
                              +{money(opt.price)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-6 pt-4 pb-6 space-y-4" style={{ borderTop: '1px solid var(--color-line)' }}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink">Quantity</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                  className="grid place-items-center w-8 h-8 rounded-full"
                  style={{ border: '1px solid var(--color-line-2)' }}
                >
                  −
                </button>
                <span className="tnum font-semibold w-5 text-center text-ink">{qty}</span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  aria-label="Increase quantity"
                  className="grid place-items-center w-8 h-8 rounded-full"
                  style={{ background: 'var(--color-ink)', color: 'var(--color-ink-inverse)' }}
                >
                  +
                </button>
              </div>
            </div>
            <button
              onClick={handleConfirm}
              disabled={missingRequired.length > 0}
              className="btn btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontSize: '1rem' }}
            >
              Add {qty > 1 ? `${qty} · ` : ''}{money(unitPrice * qty)}
            </button>
            {missingRequired.length > 0 && (
              <p className="text-center text-xs" style={{ color: 'var(--color-spice)' }}>
                Select {missingRequired.map((s) => s.name).join(', ')} to continue
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
